export const TemplateResponseType = {
    Single: 1,
    Multiple: 2,
}

export const TemplateQuestionResponseType = {
    Single: 1,
    Multiple: 2,
}
export const ControlType = {
    TextAnswer: 1,
    Number: 2,
    Checkbox: 3,
    DateAndTime: 4,
    Media: 5,
    Slider: 6,
    Signature: 7,
    Date: 10,
    Time: 11,
    TextArea: 13,
    Label: 14,
    Header: 15,
    MulltiResponse: 12,
    AutoNumber: 18,
}

/** Single-response row in E_TypeOfSingleResponses. Never match this id by itself. */
export const FORMULA_RESPONSE_ID = 19

const formulaResponseText = item =>
    (item?.DisplayText || '').trim().toLowerCase()

/**
 * Type-picker row for Formula. Multiple-choice groups can also use id 19,
 * so a group (Items / GroupId) is never treated as this response type.
 */
export const isFormulaResponseItem = item => {
    if (!item) return false
    if (formulaResponseText(item) === 'formula') return true
    const isGroup = item.GroupId != null && Array.isArray(item.Items)
    return Number(item.Id) === FORMULA_RESPONSE_ID && !isGroup
}

/** Saved question: single response AND response id 19. */
export const isFormulaQuestion = item => {
    if (!item) return false
    const type = Number(item.TypeOfResponse ?? item.QuestionType)
    if (type !== TemplateResponseType.Single) return false
    return Number(item.QuestionResponseId) === FORMULA_RESPONSE_ID
}

/** Number answer a formula is allowed to read. */
export const isNumberInputQuestion = item => {
    if (!item) return false
    const type = Number(item.TypeOfResponse ?? item.QuestionType)
    if (type !== TemplateResponseType.Single) return false
    return Number(item.QuestionResponseId) === ControlType.Number
}

/** Single-line text box. Formula dropdown can map a cell to one of these. */
export const isTextInputQuestion = item => {
    if (!item) return false
    const type = Number(item.TypeOfResponse ?? item.QuestionType)
    if (type !== TemplateResponseType.Single) return false
    return Number(item.QuestionResponseId) === ControlType.TextAnswer
}

/**
 * Formula is shown whenever the type list includes it (QA and production).
 * Optional REACT_APP_FORMULA_ALLOWED_COMPANY_IDS still limits it to those ids.
 */
// capabilities = user.Capabilities from the login. CanUseFormula is the backend's rollout gate
// (FormulaQuestion:AllowedCompanyIds) - the same rule the template save enforces, so the builder
// no longer offers Formula to a company whose save would fail with FORMULA_NOT_ENABLED.
// An older backend without the flag falls back to the build setting below.
export const isFormulaQuestionCreationEnabled = (companyId, capabilities) => {
    if (capabilities && typeof capabilities.CanUseFormula === 'boolean') return capabilities.CanUseFormula
    const raw = process.env.REACT_APP_FORMULA_ALLOWED_COMPANY_IDS
    if (raw != null && String(raw).trim() !== '') {
        const allowed = String(raw)
            .split(',')
            .map(part => Number(String(part).trim()))
            .filter(id => Number.isFinite(id) && id > 0)
        if (allowed.length) return allowed.includes(Number(companyId))
    }
    return true
}

/** Template / inspection questions that only display content (no answer input). */
export const isDisplayOnlyInspectionQuestion = item => {
    // QuestionResponseId doubles as a response-group id for multiple-choice questions;
    // only single-response rows can be display-only (label/header/auto-number).
    if (item?.TypeOfResponse !== TemplateResponseType.Single) {
        return false
    }

    const rtId = Number(item?.QuestionResponseId)
    return (
        rtId === ControlType.Label ||
        rtId === ControlType.Header ||
        rtId === ControlType.AutoNumber
    )
}

export const isAutoNumberResponseItem = item => {
    if (!item) return false
    if (Number(item.Id) === ControlType.AutoNumber) return true
    return (item.DisplayText || '').trim().toLowerCase() === 'auto number'
}

/** Header / Label / Auto Number — score is not applicable. */
export const isNonScorableResponseItem = item => {
    if (!item) return false
    const id = Number(item.Id)
    if (
        id === ControlType.Label ||
        id === ControlType.Header ||
        id === ControlType.AutoNumber
    ) {
        return true
    }
    const text = (item.DisplayText || '').trim().toLowerCase()
    if (text === 'formula') return true
    if (
        Number(item.Id) === FORMULA_RESPONSE_ID &&
        !(item.GroupId != null && Array.isArray(item.Items))
    ) {
        return true
    }
    return text === 'label' || text === 'header' || text === 'auto number'
}

/** Default "Start from" in Auto Number config UI and new questions. */
export const AUTO_NUMBER_DEFAULT_START = '1'

/** Zero-pad width for alpha-numeric auto numbers (e.g. AB000002). */
export const AUTO_NUMBER_PAD_LENGTH = 6

/** API: 1 = Numeric, 2 = Alpha numeric */
export const AutoNumberType = {
    Numeric: 1,
    AlphaNumeric: 2,
}

const readAutoNumberString = (qds, primaryKey, legacyKey) => {
    if (qds[primaryKey] != null) return String(qds[primaryKey])
    if (qds[legacyKey] != null) return String(qds[legacyKey])
    return ''
}

const readAutoNumberType = qds => {
    const primary = Number(qds.AutoNumberType)
    if (primary) return primary
    const legacy = Number(qds.AutoNumbericType)
    if (legacy) return legacy
    return 0
}

export const parseAutoNumberConfig = source => {
    const qds = source?.QuestionDisplaySetting || source || {}
    let type = readAutoNumberType(qds)
    if (!type && source?.IsNumeric) type = AutoNumberType.Numeric
    if (!type && source?.IsAlphaNumeric) type = AutoNumberType.AlphaNumeric
    const prefix = readAutoNumberString(qds, 'AutoNumberPreFix', 'Prefix')
    const startFrom = readAutoNumberString(qds, 'AutoNumberStartFrom', 'StartFrom')
    const padWidthRaw = qds.AutoNumberPadWidth
    const padWidth =
        padWidthRaw != null && padWidthRaw !== ''
            ? Number(padWidthRaw)
            : 0
    return {
        AutoNumberType: type || 0,
        AutoNumberPreFix: prefix,
        AutoNumberStartFrom: startFrom,
        AutoNumberPadWidth: Number.isFinite(padWidth) ? padWidth : 0,
    }
}

export const buildAutoNumberDisplaySetting = (baseSetting, autoConfig) => {
    const base = baseSetting || {}
    const cfg = autoConfig || {}
    const type = Number(cfg.AutoNumberType) || 0
    return {
        SettingId: base.SettingId || 0,
        IsNoteAllowed: base.IsNoteAllowed ?? true,
        IsMediaAllowed: base.IsMediaAllowed ?? true,
        QuestionId: base.QuestionId || 0,
        ...(type
            ? {
                AutoNumberType: type,
                AutoNumberPreFix:
                    type === AutoNumberType.AlphaNumeric
                        ? cfg.AutoNumberPreFix || ''
                        : '',
                AutoNumberStartFrom: cfg.AutoNumberStartFrom || '',
                ...(type === AutoNumberType.AlphaNumeric
                    ? {
                        AutoNumberPadWidth:
                            cfg.AutoNumberPadWidth != null &&
                            cfg.AutoNumberPadWidth !== ''
                                ? Number(cfg.AutoNumberPadWidth)
                                : 0,
                    }
                    : {}),
            }
            : {}),
    }
}

export const getAutoNumberSummaryLabel = config => {
    const cfg = config || {}
    const type = Number(cfg.AutoNumberType)
    if (type === AutoNumberType.Numeric) {
        const start = String(cfg.AutoNumberStartFrom || '').trim()
        return start ? `Numeric · ${start}` : 'Numeric'
    }
    if (type === AutoNumberType.AlphaNumeric) {
        const prefix = String(cfg.AutoNumberPreFix || '').trim()
        const start = String(cfg.AutoNumberStartFrom || '').trim()
        if (prefix && start) return `Alpha numeric · ${prefix}${start}`
        if (prefix) return `Alpha numeric · ${prefix}`
        if (start) return `Alpha numeric · ${start}`
        return 'Alpha numeric'
    }
    return ''
}

/** Numeric Start from: digits only, no leading zero, minimum 1. */
export const normalizeNumericAutoNumberStartFrom = value => {
    const digits = String(value ?? '').replace(/\D/g, '')
    if (!digits) return ''
    const withoutLeadingZeros = digits.replace(/^0+/, '')
    return withoutLeadingZeros
}

export const isValidNumericAutoNumberStartFrom = value => {
    const s = String(value ?? '').trim()
    if (!s) return false
    return /^[1-9]\d*$/.test(s)
}

/** Alpha numeric Start from input: digits only; keeps leading zeros in UI. */
export const normalizeAlphaNumericAutoNumberStartFrom = value =>
    String(value ?? '').replace(/\D/g, '')

/**
 * Split alpha numeric Start from for API:
 * - AutoNumberStartFrom: digits without leading zeros (e.g. "1")
 * - AutoNumberPadWidth: count of leading zeros before significant digits (e.g. 4 for "00001")
 */
export const parseAlphaNumericAutoNumberStartFrom = value => {
    const digits = normalizeAlphaNumericAutoNumberStartFrom(value)
    if (!digits) {
        return {
            valid: false,
            display: '',
            startFrom: '',
            padWidth: 0,
        }
    }

    const significant = digits.replace(/^0+/, '')
    if (!significant || !/^[1-9]\d*$/.test(significant)) {
        return {
            valid: false,
            display: digits,
            startFrom: '',
            padWidth: 0,
        }
    }

    const padWidth = digits.length - significant.length

    return {
        valid: true,
        display: digits,
        startFrom: significant,
        padWidth,
    }
}

export const formatAlphaNumericStartFromDisplay = (startFrom, padWidth) => {
    const significant = String(startFrom ?? '')
        .replace(/\D/g, '')
        .replace(/^0+/, '')
    if (!significant) return ''
    const zeros = Math.max(0, Number(padWidth) || 0)
    return `${'0'.repeat(zeros)}${significant}`
}

export const getAlphaNumericPadWidth = config => {
    const parsed = parseAlphaNumericAutoNumberStartFrom(
        formatAlphaNumericStartFromDisplay(
            config?.AutoNumberStartFrom,
            config?.AutoNumberPadWidth,
        ) || config?.AutoNumberStartFrom,
    )
    if (parsed.valid) return parsed.padWidth
    return Number(config?.AutoNumberPadWidth) || 0
}

export const getAlphaNumericNumericWidth = config => {
    const significant = String(config?.AutoNumberStartFrom ?? '')
        .replace(/^0+/, '')
        .replace(/\D/g, '')
    const padWidth = getAlphaNumericPadWidth(config)
    return padWidth + (significant.length || 1)
}

export const isValidAlphaNumericAutoNumberStartFrom = value =>
    parseAlphaNumericAutoNumberStartFrom(value).valid

export const isAutoNumberConfigComplete = config => {
    const type = Number(config?.AutoNumberType)
    if (!type) return false
    const start = String(config?.AutoNumberStartFrom ?? '').trim()
    if (!start) return false
    if (type === AutoNumberType.Numeric) {
        return isValidNumericAutoNumberStartFrom(start)
    }
    if (type === AutoNumberType.AlphaNumeric) {
        return (
            String(config?.AutoNumberPreFix ?? '').trim() !== '' &&
            isValidAlphaNumericAutoNumberStartFrom(
                formatAlphaNumericStartFromDisplay(
                    config?.AutoNumberStartFrom,
                    config?.AutoNumberPadWidth,
                ) || config?.AutoNumberStartFrom,
            )
        )
    }
    return true
}

export const getAutoNumberTypeLabel = type => {
    const t = Number(type)
    if (t === AutoNumberType.Numeric) return 'Numeric'
    if (t === AutoNumberType.AlphaNumeric) return 'Alpha numeric'
    return ''
}

/** Build assigned auto number for inspection (e.g. AB000002). */
export const formatAutoNumberAssignedValue = (config, sequenceNumber) => {
    const cfg = config || {}
    const type = Number(cfg.AutoNumberType)
    const num = Number(sequenceNumber)
    if (!type || !Number.isFinite(num) || num < 0) return ''
    if (type === AutoNumberType.AlphaNumeric) {
        const prefix = String(cfg.AutoNumberPreFix || '').trim()
        const width = getAlphaNumericNumericWidth(cfg)
        const padded = String(Math.floor(num)).padStart(width, '0')
        return prefix ? `${prefix}${padded}` : padded
    }
    if (type === AutoNumberType.Numeric) {
        return String(Math.floor(num))
    }
    return ''
}

export const parseAutoNumberSequenceFromResponse = (response, config) => {
    if (response == null || String(response).trim() === '') return null
    const raw = String(response).trim()
    const type = Number(config?.AutoNumberType)
    if (type === AutoNumberType.AlphaNumeric) {
        const prefix = String(config?.AutoNumberPreFix || '').trim()
        let numericPart = raw
        if (prefix && raw.startsWith(prefix)) {
            numericPart = raw.slice(prefix.length)
        }
        const parsed = parseInt(numericPart, 10)
        return Number.isFinite(parsed) ? parsed : null
    }
    const parsed = parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : null
}

/**
 * Assign the next auto number when a new inspection starts (empty answer).
 * Increments AutoNumberStartFrom on the question for the next inspection.
 */
export const assignAutoNumberForInspectionQuestion = question => {
    const cfg = parseAutoNumberConfig(question)
    if (!isAutoNumberConfigComplete(cfg)) {
        return { question, changed: false }
    }

    const existing = question?.Answers?.SingleResponse?.Response
    if (existing != null && String(existing).trim() !== '') {
        return { question, changed: false }
    }

    const parsedStart = parseInt(cfg.AutoNumberStartFrom, 10)
    const sequence = Number.isFinite(parsedStart) ? parsedStart : 1
    const assigned = formatAutoNumberAssignedValue(cfg, sequence)
    if (!assigned) {
        return { question, changed: false }
    }

    const nextStartFrom = String(sequence + 1)
    const nextSetting = buildAutoNumberDisplaySetting(
        question.QuestionDisplaySetting,
        {
            ...cfg,
            AutoNumberStartFrom: nextStartFrom,
            AutoNumberPadWidth:
                Number(cfg.AutoNumberType) === AutoNumberType.AlphaNumeric
                    ? cfg.AutoNumberPadWidth
                    : 0,
        },
    )

    return {
        question: {
            ...question,
            QuestionDisplaySetting: nextSetting,
            Answers: {
                ...(question.Answers || {}),
                SingleResponse: {
                    ...(question.Answers?.SingleResponse || {}),
                    Response: assigned,
                    IsSelected: true,
                },
                IsAnswered: true,
            },
            IsAnswered: true,
        },
        changed: true,
    }
}

// 2026-10-27: value shown for an auto-number before the server has assigned one (template preview), in the
// SERVER's format (AutoNumberSequenceService + AutoNumberFormatter) - the preview showed "1" for a Seq question with
// pad width 4 while inspections get "0001". Scope "Seq" = minimum width (prefix only for alpha numeric), "Year" =
// year + prefix + minimum width, no scope = PadWidth zeros in front (legacy). "ClientType" needs the customer: as before.
export const formatAutoNumberPreviewValue = (source, sequenceNumber, now = new Date()) => {
    const cfg = parseAutoNumberConfig(source)
    const qds = source?.QuestionDisplaySetting || source || {}
    const scope = String(qds.AutoNumberScope || '').trim().toLowerCase()
    const type = Number(cfg.AutoNumberType)
    const num = Number(sequenceNumber)
    if (!type || !Number.isFinite(num) || num < 0) return ''
    const pad = Math.min(Math.max(Number(cfg.AutoNumberPadWidth) || 0, 0), 20)
    const digits = String(Math.floor(num))
    const prefix = String(cfg.AutoNumberPreFix || '')
    if (scope === 'seq') {
        const n = digits.padStart(pad, '0')
        return type === AutoNumberType.AlphaNumeric ? `${prefix}${n}` : n
    }
    if (scope === 'year') return `${now.getUTCFullYear()}${prefix}${digits.padStart(pad, '0')}`
    if (scope !== 'clienttype' && type === AutoNumberType.Numeric) return '0'.repeat(pad) + digits
    return formatAutoNumberAssignedValue(cfg, num)
}

/** Inspection UI: prefer server-assigned answer, then formatted config fallback. */
export const getAutoNumberInspectionDisplay = item => {
    const cfg = parseAutoNumberConfig(item)
    const type = Number(cfg.AutoNumberType)
    const prefix = String(cfg.AutoNumberPreFix || '').trim()
    const startFrom =
        cfg.AutoNumberStartFrom != null && cfg.AutoNumberStartFrom !== ''
            ? String(cfg.AutoNumberStartFrom).trim()
            : ''

    const raw = item?.Answers?.SingleResponse?.Response
    let value =
        raw != null && String(raw).trim() !== '' ? String(raw).trim() : ''
    if (!value) {
        const parsedStart = parseInt(startFrom, 10)
        // same as the server: StartFrom when it is 1 or more, else 1
        const sequence = Number.isFinite(parsedStart) && parsedStart >= 1 ? parsedStart : 1
        value = formatAutoNumberPreviewValue(item, sequence) || ''
    }

    const typeLabel = getAutoNumberTypeLabel(type)

    return {
        value,
        hasValue: value.length > 0,
        type,
        typeLabel,
        prefix,
        startFrom,
        showPrefix: type === AutoNumberType.AlphaNumeric && prefix.length > 0,
        showStartFrom: startFrom.length > 0,
    }
}

export const ChangeAction = {
    None: 0, // Optional: for safety fallback
    New: 1, // New item to create
    Update: 2, // Existing item to update
    Delete: 3, // Item to delete (soft delete)
    SectionChange: 4, // Section metadata changed
    SectionQuestionChange: 5, // Section has question changes
}
